import React, { useState, useContext } from 'react';

import { useTranslation } from 'react-i18next';

import { useEditor, useNode } from '@craftjs/core';

import MyButton from '@common/components/Button/MyButton';

import ExtensionIcon from '@mui/icons-material/Extension';

import TextPlaceholderModal from './TextPlaceholderModal';
import { getParentSrc } from '@common/helpers/helpers';
import { INIT_TEXT_MODAL_STATE } from '@common/constants';
import { useTemplateDetailsStore } from '@store/templateDetailsStore';

const getSelectedId = (selected) =>
  selected.size > 0 ? Array.from(selected)[0] : null;

const getSelectedElement = (selectedId) => window.nodeRefs?.[selectedId];

const isNotSelected = (selection) => !selection || selection.rangeCount === 0;

const AddTextPlaceholder = () => {
  const { t } = useTranslation();

  const { selected, query } = useEditor((state) => ({
    selected: state.events.selected,
  }));

  const {
    actions: { setProp },
    parentId,
  } = useNode(({ data: { props, parent }, events }) => {
    return {
      placeholderValue: props.text,
      isActive: events?.selected,
      parentId: parent,
    };
  });

  const source = useTemplateDetailsStore((s) => s.source);
  const [showModal, setShowModal] = useState(INIT_TEXT_MODAL_STATE);

  const onAddPlaceholderClick = () => {
    const selectedId = getSelectedId(selected);
    const selection = window?.getSelection();

    if (selectedId && !isNotSelected(selection)) {
      const selectedElement = getSelectedElement(selectedId);
      const range = selection.getRangeAt(0);

      if (!selectedElement.contains(range.commonAncestorContainer)) return;

      const { parentSource, parentListSrc } = getParentSrc({ query, parentId });

      setShowModal({
        show: true,
        listSource: parentSource,
        parentListSrc,
        parent: '',
        showRootFields: typeof parentSource === 'undefined',
        action: 'source',
        selections: {
          range: range,
          selection: selection,
          selectedElement: selectedElement,
          selectedId: selectedId,
        },
      });
    }
  };

  const onCancelClick = () => {
    setShowModal(INIT_TEXT_MODAL_STATE);
  };

  function copyClassesAndInlineStyle(fromEl, toEl) {
    if (!(fromEl instanceof Element) || !(toEl instanceof Element)) return;
    fromEl.classList.forEach((c) => toEl.classList.add(c));
    const src = fromEl.getAttribute('style');
    if (src) {
      const existing = toEl.getAttribute('style') || '';
      toEl.setAttribute('style', [src, existing].filter(Boolean).join(';'));
    }
  }

  function splitAncestorsAroundMarker(
    marker,
    root,
    splitTags = new Set(['SPAN']),
  ) {
    const chain = [];
    let parent = marker.parentElement;

    while (parent && parent !== root && splitTags.has(parent.tagName)) {
      const left = parent;
      const right = left.cloneNode(false);

      let n = marker.nextSibling;
      while (n) {
        const nxt = n.nextSibling;
        right.appendChild(n);
        n = nxt;
      }

      left.parentNode.insertBefore(right, left.nextSibling);
      left.parentNode.insertBefore(marker, right);

      chain.push({ left, right });

      if (!left.firstChild) left.remove();
      if (!right.firstChild) right.remove();

      parent = marker.parentElement;
    }

    return { chain, innermost: chain[0] || null };
  }

  function insertPlaceholderAtSelection(
    rootP,
    range,
    { text, placeholderId, splitLinks = true } = {},
  ) {
    const sel = window.getSelection?.();
    if (!range || !sel) return null;

    if (!range.collapsed) {
      range.deleteContents();
      range.collapse(true);
    }

    const marker = document.createElement('span');
    marker.setAttribute('data-ins-marker', '1');

    range.insertNode(marker);

    const splitSet = new Set(['SPAN']);
    if (splitLinks) splitSet.add('A');
    const { innermost } = splitAncestorsAroundMarker(marker, rootP, splitSet);

    const ph = document.createElement('span');
    ph.setAttribute('data-placeholder', placeholderId);
    ph.setAttribute('contenteditable', 'false');

    if (innermost) {
      const styleSource = innermost.left?.isConnected
        ? innermost.left
        : innermost.right?.isConnected
          ? innermost.right
          : innermost.left || innermost.right;
      if (styleSource) copyClassesAndInlineStyle(styleSource, ph);
    }

    Object.assign(ph.style, {
      display: 'inline-block',
      width: 'auto',
      margin: 0,
      'user-select': 'text',
      '-webkit-user-select': 'text',
    });

    ph.appendChild(document.createTextNode(text));

    marker.replaceWith(ph);

    const nr = document.createRange();
    nr.setStartAfter(ph);
    nr.collapse(true);
    sel.removeAllRanges();
    sel.addRange(nr);

    return ph;
  }

  const onDialogOk = (checkedField) => {
    const { range, selectedElement, selectedId } = showModal.selections;
    if (!range || !selectedElement) return;

    insertPlaceholderAtSelection(selectedElement, range, {
      text: `{{${checkedField}}}`,
      placeholderId: selectedId,
      splitLinks: true,
    });

    setProp((props) => {
      props.text = selectedElement.innerHTML;
    }, 500);
    setShowModal(INIT_TEXT_MODAL_STATE);
  };

  return (
    <>
      <TextPlaceholderModal
        documentJson={source?.structure || {}}
        currentFormula=''
        currentValue={''}
        showModal={showModal}
        onOkClick={onDialogOk}
        onCancelClick={onCancelClick}
      />
      <MyButton
        color={'primary'}
        onMouseDown={onAddPlaceholderClick}
        startIcon={<ExtensionIcon />}
      >
        {t('add_placeholder')}
      </MyButton>
    </>
  );
};

export default AddTextPlaceholder;
